import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { normalizeImageUrl, onImageErrorSetFallback } from '../utils/imageFallback';

const Cart = () => {
  const { t } = useTranslation('checkout');
  const { cart, loading, updateQuantity, removeFromCart, clearCart } = useCart();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [shippingConfig, setShippingConfig] = useState({ base_fee: 60, free_shipping_threshold: 0 });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/settings/shipping');
        if (res.data?.success && res.data.config) {
          setShippingConfig({
            base_fee: Number(res.data.config.base_fee) || 60,
            free_shipping_threshold: Number(res.data.config.free_shipping_threshold) || 0,
          });
        }
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  const subtotal = cart?.subtotal || 0;
  const shippingFee =
    shippingConfig.free_shipping_threshold > 0 && subtotal >= shippingConfig.free_shipping_threshold
      ? 0
      : Number(shippingConfig.base_fee || 0);
  const total = subtotal + shippingFee;

  const totalSavings = useMemo(() => {
    if (!cart?.items) return 0;
    return cart.items.reduce((sum, item) => {
      const original = Number(item.original_price || item.sale_price || 0);
      const sale = Number(item.display_price || item.sale_price || 0);
      return sum + Math.max(0, original - sale) * Number(item.quantity || 0);
    }, 0);
  }, [cart]);

  const handleRemove = useCallback(
    (item) => {
      // GA4 remove_from_cart
      if (window.dataLayer) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'remove_from_cart',
          ecommerce: {
            currency: 'TWD',
            value: Number(item.display_price || item.sale_price || 0) * Number(item.quantity || 0),
            items: [
              {
                item_id: item.sku || String(item.product_id),
                item_name: item.name,
                price: Number(item.display_price || item.sale_price || 0),
                quantity: Number(item.quantity || 0),
              },
            ],
          },
        });
      }
      removeFromCart(item.product_id);
    },
    [removeFromCart]
  );

  const beginCheckout = useCallback(() => {
    if (cart?.items?.length) {
      if (window.dataLayer) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'begin_checkout',
          ecommerce: {
            currency: 'TWD',
            value: subtotal,
            items: cart.items.map((item, index) => ({
              item_id: item.sku || String(item.product_id),
              item_name: item.name,
              price: Number(item.display_price || item.sale_price || 0),
              quantity: Number(item.quantity || 0),
              index,
            })),
          },
        });
      }
    }
    navigate('/checkout');
  }, [cart, subtotal, navigate]);

  if (!cart || cart.items === undefined) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography>{t('cart.loading')}</Typography>
      </Container>
    );
  }

  if (cart.items.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <ShoppingCartIcon sx={{ fontSize: 72, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight={900} sx={{ mb: 0.5 }}>
            {t('cart.empty')}
          </Typography>
          <Typography color="text.secondary">{t('cart.empty_hint')}</Typography>
          <Button variant="contained" sx={{ mt: 2, fontWeight: 900 }} onClick={() => navigate('/products')}>
            {t('cart.continue_shopping')}
          </Button>
        </Box>
      </Container>
    );
  }

  const freeShipRemain =
    shippingConfig.free_shipping_threshold > 0
      ? Math.max(0, shippingConfig.free_shipping_threshold - subtotal)
      : 0;
  const freeShipProgress =
    shippingConfig.free_shipping_threshold > 0
      ? Math.min(100, (subtotal / shippingConfig.free_shipping_threshold) * 100)
      : 0;

  return (
    <Box sx={{ pb: isMobile ? 10 : 0 }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={950}>
            {t('cart.title', { count: cart.itemCount })}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' } }}>
            <Button variant="outlined" onClick={() => navigate('/products')} sx={{ fontWeight: 900 }}>
              {t('cart.continue_shopping')}
            </Button>
            <Button variant="text" color="error" onClick={clearCart} disabled={loading} sx={{ fontWeight: 900 }}>
              {t('cart.clear_cart')}
            </Button>
          </Stack>
        </Stack>

        {shippingConfig.free_shipping_threshold > 0 && (
          <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            {freeShipRemain === 0 ? (
              <Alert severity="success" icon={<LocalOfferIcon />}>
                {t('cart.free_shipping_reached')}
              </Alert>
            ) : (
              <Alert severity="info" icon={<LocalOfferIcon />}>
                {t('cart.free_shipping_remaining', { amount: Math.ceil(freeShipRemain).toLocaleString() })}
              </Alert>
            )}
            <LinearProgress variant="determinate" value={freeShipProgress} sx={{ mt: 1.5, height: 8, borderRadius: 6 }} />
          </Paper>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 360px' }, gap: 2 }}>
          <Box>
            {isMobile ? (
              <Stack spacing={1.5}>
                {cart.items.map((item) => (
                  <Card key={item.id} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }} elevation={0}>
                    <CardContent sx={{ p: 2 }}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box
                          component="img"
                          src={normalizeImageUrl(item.image_url, item.name)}
                          alt={item.name}
                          sx={{ width: 86, height: 86, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                          onError={(e) => onImageErrorSetFallback(e, item.name)}
                          onClick={() => navigate(`/products/${item.product_id}`)}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={900} sx={{ lineHeight: 1.35 }} noWrap>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            ${Number(item.display_price || item.sale_price || 0).toLocaleString()}
                            {item.original_price ? (
                              <Typography component="span" variant="caption" sx={{ ml: 1, textDecoration: 'line-through' }}>
                                ${Number(item.original_price).toLocaleString()}
                              </Typography>
                            ) : null}
                          </Typography>

                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                            <IconButton size="small" onClick={() => updateQuantity(item.product_id, item.quantity - 1)} disabled={loading || item.quantity <= 1}>
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography sx={{ minWidth: 28, textAlign: 'center', fontWeight: 900 }}>{item.quantity}</Typography>
                            <IconButton size="small" onClick={() => updateQuantity(item.product_id, item.quantity + 1)} disabled={loading || item.quantity >= item.stock}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                            <Box sx={{ flex: 1 }} />
                            <Typography fontWeight={950} color="error">
                              ${Number(item.item_total || 0).toLocaleString()}
                            </Typography>
                          </Stack>
                        </Box>
                        <IconButton color="error" onClick={() => handleRemove(item)} disabled={loading}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <TableContainer component={Paper} sx={{ borderRadius: 2 }} elevation={0}>
                <Table>
                  <TableHead sx={{ bgcolor: 'background.default' }}>
                    <TableRow>
                      <TableCell>{t('cart.product')}</TableCell>
                      <TableCell align="center">{t('cart.unit_price')}</TableCell>
                      <TableCell align="center">{t('cart.quantity')}</TableCell>
                      <TableCell align="center">{t('cart.subtotal')}</TableCell>
                      <TableCell align="center">{t('cart.remove')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cart.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Box
                              component="img"
                              src={normalizeImageUrl(item.image_url, item.name)}
                              alt={item.name}
                              sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                              onError={(e) => onImageErrorSetFallback(e, item.name)}
                              onClick={() => navigate(`/products/${item.product_id}`)}
                            />
                            <Box>
                              <Typography fontWeight={900}>{item.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                SKU: {item.sku}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Typography fontWeight={900} color="error">
                            ${Number(item.display_price || item.sale_price || 0).toLocaleString()}
                          </Typography>
                          {item.original_price ? (
                            <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through', display: 'block' }}>
                              ${Number(item.original_price).toLocaleString()}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                            <IconButton size="small" onClick={() => updateQuantity(item.product_id, item.quantity - 1)} disabled={loading || item.quantity <= 1}>
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography sx={{ minWidth: 28, textAlign: 'center', fontWeight: 900 }}>{item.quantity}</Typography>
                            <IconButton size="small" onClick={() => updateQuantity(item.product_id, item.quantity + 1)} disabled={loading || item.quantity >= item.stock}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Typography fontWeight={950} color="error">
                            ${Number(item.item_total || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton color="error" onClick={() => handleRemove(item)} disabled={loading}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          <Paper sx={{ p: 2.5, borderRadius: 2, height: 'fit-content', position: { md: 'sticky' }, top: { md: 100 } }} elevation={0}>
            <Typography fontWeight={950} sx={{ mb: 1 }}>
              {t('cart.order_summary')}
            </Typography>
            <Divider sx={{ my: 1.5 }} />

            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">{t('cart.subtotal')}</Typography>
                <Typography>${Number(subtotal).toLocaleString()}</Typography>
              </Stack>

              {totalSavings > 0 && (
                <Stack direction="row" justifyContent="space-between" sx={{ color: 'success.main' }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <LocalOfferIcon sx={{ fontSize: 18 }} />
                    <Typography color="success.main">{t('cart.saved')}</Typography>
                  </Stack>
                  <Typography color="success.main" fontWeight={900}>
                    -${Math.round(totalSavings).toLocaleString()}
                  </Typography>
                </Stack>
              )}

              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">{t('cart.shipping_fee')}</Typography>
                <Typography>{shippingFee === 0 ? <Chip label={t('cart.free_shipping')} size="small" color="success" /> : `$${Number(shippingFee).toLocaleString()}`}</Typography>
              </Stack>

              <Divider sx={{ my: 1 }} />

              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography fontWeight={900}>{t('cart.total')}</Typography>
                <Typography fontWeight={950} color="error" fontSize="1.35rem">
                  ${Number(total).toLocaleString()}
                </Typography>
              </Stack>

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={beginCheckout}
                data-testid="cart-checkout"
                sx={{ mt: 1.5, py: 1.4, fontWeight: 950 }}
              >
                {t('cart.checkout')}
              </Button>
            </Stack>
          </Paper>
        </Box>
      </Container>

      {isMobile && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 56, // above mobile bottom nav
            borderTop: '1px solid',
            borderColor: 'divider',
            p: 1.5,
            zIndex: 1090,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {t('cart.total')}
              </Typography>
              <Typography fontWeight={950} color="error" sx={{ lineHeight: 1.2 }}>
                ${Number(total).toLocaleString()}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={beginCheckout}
              data-testid="cart-checkout-mobile"
              fullWidth
              sx={{ py: 1.2, fontWeight: 950 }}
            >
              {t('cart.checkout')}
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default Cart;


